import { inject, Injectable, signal } from '@angular/core';
import { IAgent, IAgentsResponse } from '../../../../../interfaces/i-agents';
import { ProfileService } from '../../../../../services/profile.service';
import { IUser } from '../../../../../interfaces/i-user-profile';
import { AGENT_BUILDER_DEFAULT_DEFINITION_VALUE } from '../constants/agent-builder.constant';
import { ApiService } from '../../../../../services/api.service';
import { AgentService } from '../../../services/agent.service';

@Injectable({
  providedIn: 'root'
})
export class AgentBuilderService {
  agent = signal<IAgent>(this.createAgentDefaultValue());

  #profileService = inject(ProfileService);
  #apiService = inject(ApiService);
  #agentService = inject(AgentService);

  constructor() {
    const owner: IUser = {
      name: this.#profileService.userProfile()!.profile.name,
      userId: this.#profileService.userProfile()!.profile.email
    };

    // Check if the owner already exists in the list of owners
    const ownerExists = this.agent().details.owners.some(existingOwner => existingOwner.userId === owner.userId);
    // Add the owner to the agent after the user has logged in. Use the
    // signal `update` method so we don't mutate the value directly and
    // ensure subscribers are notified of the change.
    if (!ownerExists) {
      this.agent.update(currentAgent => ({
        ...currentAgent,
        details: {
          ...currentAgent.details,
          owners: [...currentAgent.details.owners, owner]
        }
      }));

      this.#apiService.getAgents(this.#agentService?.getSensitivityLabelFromSession()).subscribe({
        next: (data: IAgentsResponse) => {
          const updatedData = this.#agentService.convertAgentsToArray(data);
          this.#agentService.agents.set(updatedData);
        }
      });
    }
  }

  /**
   * Reset the agent to the default values
   */
  resetAgent() {
    if (this.#profileService.userProfile()) {
      const owner: IUser = {
        name: this.#profileService.userProfile()!.profile.name,
        userId: this.#profileService.userProfile()!.profile.email
      };

      const defaultAgent = this.createAgentDefaultValue();
      defaultAgent.details.owners.push(owner);
      // Use update to replace the agent with the default values (including the owner)
      this.agent.update(() => defaultAgent);
    }
  }

  createAgentDefaultValue(): IAgent {
    const agentId = this.#generateAgentId();
    return {
      details: {
        apiHeaders: null,
        apiPayload: null,
        apiUrl: 'https://',
        // NOTE: Keep the format of the agent definition JSON as a string. Changing it will change the display in the textarea field.
        definition: AGENT_BUILDER_DEFAULT_DEFINITION_VALUE,
        description: 'Get the current time for a given location',
        keywords: [],
        id: agentId,
        isEnabled: true,
        name: 'Get Location Time',
        owners: [],
        requestManipulationCode: null,
        responseManipulationCode: null,
        sensitivityLabels: [],
        alwaysAvailable: false,
        sendMessageId: false,
        sendSessionId: false,
        sendUserInfo: false,
        sendUserPrompt: false,
        supportUrl: 'https://',
        systemMessage: null,
        users: [],
        groups: [],
        usesApi: false,
        usesGet: false
      },
      id: agentId,
      isDisabled: false,
      isUniversal: false
    };
  }

  #generateAgentId(): string {
    return crypto.randomUUID();
  }
}
